/**
 * TimelineItemFactory — JSON -> TimelineItem construction for the home
 * timeline (the larger items: logos, swirls, masked text, relative-children
 * groups, plain timeline sprites).
 *
 * Owns the per-pass item loop (pass-1 items mount into the itemsPass1 slot,
 * pass-2 into itemsPass2), the per-type construction switch and the common
 * config application. It does NOT own the item registry — createItems RETURNS
 * the built batch and HomeTimeline stores it in its shared _items array
 * (the registry the HomeItemGeometry contract's getItems serves). Region
 * creation (config.regions -> TimelineRegionManager) is threshold-machine
 * wiring and stays on HomeTimeline.
 *
 * IMPORTANT: the bodies are a verbatim extraction from HomeTimeline —
 * the empty 'field' branch, the pass-membership rules (`!itemData.pass` for
 * pass 1, `pass == 2` for pass 2) and the ParticleHitTests guide-container
 * (re)wiring at the top of every call are load-bearing quirks; keep them.
 * sceneProps is the SAME shared object the timeline created (identity is
 * load-bearing; it is only threaded into plain TimelineItem.create).
 */
import { particles } from 'app/contracts/Particles';
import type { TimelineItemFactoryDeps } from 'app/views/home/timeline/timeline.types';

import { TimelineItem } from 'app/views/home/timeline/TimelineItem';
import { MaskedText } from 'app/views/home/timeline/MaskedText';
import { RelativeChildren } from 'app/views/home/timeline/RelativeChildren';

import { TimelineItemKind } from 'app/data/Enums';

import { HomeViewConfig } from 'app/model/HomeViewConfig';
import { TimelineItemConfig } from 'app/model/TimelineItemConfig';

import ParticleHitTests from 'app/views/home/targets/ParticleHitTests';

export class TimelineItemFactory {
	private _sceneProps: particles.SharedSceneContext;
	private _config: HomeViewConfig;
	private _deps: TimelineItemFactoryDeps;

	constructor(sceneProps: particles.SharedSceneContext, config: HomeViewConfig, deps: TimelineItemFactoryDeps) {
		this._sceneProps = sceneProps;
		this._config = config;
		this._deps = deps;
	}

	//Create the larger items used in the home structure like logos, swirls, etc
	public createItems(spritesheet: PIXI.Spritesheet, pass = 1): Array<TimelineItem> {
		let slots = this._deps.getSlots();
		let items: Array<TimelineItem> = [];

		ParticleHitTests.setGuideContainer(slots.guides);

		//Pass-1 items mount into the itemsPass1 slot (behind the fields),
		//pass-2 items into itemsPass2 (above them) — JSON order within each:
		let $itemsLayer = pass == 2 ? slots.itemsPass2 : slots.itemsPass1;

		//Loop through and create all items:
		this._config.items.forEach((itemData: TimelineItemConfig) => {
			if (!itemData.disable && this._includedForWebGLType(itemData)) {
				if (itemData.type === 'field') {
				} else {
					if (pass == 1 && !itemData.pass) {
						this._createItem(items, $itemsLayer, spritesheet, itemData);
					} else if (pass == 2 && itemData.pass == 2) {
						this._createItem(items, $itemsLayer, spritesheet, itemData);
					}
				}
			}
		});

		return items;
	}

	/**
	 * webglTypes item filter: absent = the item is included for all HomeWebGL
	 * types; present = only when the active type is listed (see
	 * TimelineItemConfig.webglTypes). By-id consumers must tolerate the
	 * resulting absences — HomeTimeline._getItemsById warns on empty lookups
	 * while the /dev/home/ harness is active.
	 */
	private _includedForWebGLType(itemData: TimelineItemConfig): boolean {
		return !itemData.webglTypes || itemData.webglTypes.indexOf(this._deps.getWebGLType()) !== -1;
	}

	/** Create single item */
	private _createItem(
		items: Array<TimelineItem>,
		$itemsLayer: PIXI.Container,
		spritesheet: PIXI.Spritesheet,
		itemData: TimelineItemConfig
	): void {
		let item: TimelineItem;
		if (itemData.type === TimelineItemKind.MASKED_TEXT) {
			//Maked text setup
			item = new MaskedText();
			item = this._configItem<MaskedText>(item as MaskedText, itemData);
			item.create(itemData, {
				container: $itemsLayer,
				maskTexture: spritesheet.textures[itemData.maskTexture],
				backgroundTexture: spritesheet.textures[itemData.backgroundTexture]
			});
		} else if (itemData.type === TimelineItemKind.RELATIVE_CHILDREN) {
			//Maked text setup

			item = new RelativeChildren();
			item = this._configItem<RelativeChildren>(item as RelativeChildren, itemData);

			item.create(itemData, {
				container: $itemsLayer,
				textures: spritesheet.textures
			});
		} else {
			//A normal pixi sprite that will be animated along the timeline

			item = new TimelineItem();
			item = this._configItem<TimelineItem>(item, itemData);
			item.create(
				itemData,
				{
					container: $itemsLayer,
					texture: spritesheet.textures[itemData.texture],
					textureMobile: spritesheet.textures[itemData.textureMobile]
				},
				this._sceneProps
			);
		}

		//Push in the item
		items.push(item);
	}


	/** Config common item settings */
	private _configItem<T extends TimelineItem>(item: T, config: TimelineItemConfig): T {
		item.relativeX = config.relativeX;
		item.relativeY = config.relativeY;
		item.easePower = config.easePower;
		item.parallaxOffsetPower = config.parallaxOffsetPower;
		item.parallaxPowerMouse = config.parallaxPowerMouse;
		item.textureName = config.texture;

		if (config.alpha) {
			item.alpha = config.alpha;
		}

		if (config.tint) {
			item.tint = config.tint;
		}

		return item;
	}
}
